import { CheckIcon, CopyIcon } from 'lucide-react'

import {
  fieldButtonVariants,
  fieldIconVariants,
} from '~/components/component-library/Copyable/styles'
import { FieldCopyableProps } from '~/components/component-library/Copyable/types'
import {
  SENSITIVE_ANALYTICS_ATTRIBUTES,
  SESSION_REPLAY_BLOCK_CLASS,
  truncate,
} from '~/components/component-library/Copyable/utils'
import { Group } from '~/components/component-library/Group'
import { useCopyValue } from '~/hooks/useCopyValue'
import { cn } from '~/utils/tailwind-utils'

export const FieldCopyable = ({
  value,
  displayValue,
  size = 'base',
  truncateMiddle = false,
  successMessage,
  label,
  tooltip,
  footer,
  description,
  sensitive = false,
}: FieldCopyableProps) => {
  const { isCopied, handleCopy, handleCopySecret } = useCopyValue()
  const copy = sensitive ? handleCopySecret : handleCopy
  const truncated = truncate(value, truncateMiddle)
  const showSwap = !displayValue && truncateMiddle && truncated !== value
  const copyMessage = successMessage ?? (label ? `${label} copied` : 'Copied')

  return (
    <Group
      label={label}
      tooltip={tooltip}
      footer={footer}
      description={description}
    >
      <button
        type="button"
        onClick={(event) => {
          event.preventDefault()
          event.stopPropagation()
          copy(value, copyMessage)
        }}
        aria-label={`Copy ${label ?? (sensitive ? 'value' : value)}`}
        className={cn('group', fieldButtonVariants({ size }), {
          [SESSION_REPLAY_BLOCK_CLASS]: sensitive,
        })}
        {...(sensitive && SENSITIVE_ANALYTICS_ATTRIBUTES)}
      >
        {showSwap ? (
          <>
            <span className="min-w-0 truncate text-primary group-hover:hidden">
              {truncated}
            </span>
            <span className="hidden min-w-0 truncate text-primary group-hover:inline-block">
              {value}
            </span>
          </>
        ) : (
          <span className="min-w-0 truncate text-primary">
            {displayValue ?? value}
          </span>
        )}
        {isCopied ? (
          <CheckIcon className={fieldIconVariants({ size })} />
        ) : (
          <CopyIcon className={fieldIconVariants({ size })} />
        )}
      </button>
    </Group>
  )
}
